/**
 *   🌚🌝 Room Context 👌🏻
 *   Author: David Nuñez (CACAOS)
 *   Date: September 2018
 */

// Dependencies
import React, { Component } from 'react';
import moment from 'moment';
import DataService from 'js/modules/dataservice';
// import utils from 'js/modules/utils';
// import cm from 'js/modules/cm';

// Modules
import constants from 'js/modules/constants';
import {
  applyAmenitiesFilter,
  applyLevelsFilter,
  applyRoomViewFilter,
  applyRoomTypeFilter,
} from 'js/modules/roomFilters';
import gatracking from '../../modules/gatracking';
import { initUpliftNonPayment, nonPaymentUplift, upliftJS } from '../global/Uplift';

// Variable Declaration
const RoomContext = React.createContext();
const { SUCCESS } = constants.STATUS;

class RoomProvider extends Component {
  state = {
    fields: {
      rstCode: null,
      roomCode: null,
      checkInDate: null,
      checkOutDate: null,
      adults: null,
      children: null,
      infants: null,
      ssgNumber: 0,
      airIncluded: false,
    },
    status: 'loading',
    roomData: {},
    roomsFiltered: [],
    mismatchedRooms: [],
    filters: {
      roomView: [],
      amenities: [],
      levels: [],
      roomTypes: [],
      sortBy: 0,
    },
    roomInputs: {
      roomCategoryViews: [],
      amenities: [],
      roomLevels: [],
      roomTypes: [],
    },
    isModalOpen: {
      suitesLevels: false,
      infantToChildren: false,
    },
    showRoomInputs: false,
    showLoader: false,
    scrollableRoomId: '',
    vacationData: {},
    showRoomDetails: false,
    summaryRoomCode: '',
    step2: {},
    showFlightWarning: false,
    hasNewDates: false,
    currentRoomAvailability: '',
    getRoomsPromise: null,
    previouslyViewedRoom: null,
    upliftIsReady: false,
    upliftAlreadyUp: false,
    showLevelInfo: false
  }

  async componentDidMount() {
    window.onpopstate = this.onBackButtonEvent;

    this.fetchSummary();

    // Get Gateways
    const gatewaysResponse = await DataService.readGateways();

    /** JSON that enables/disables the Uplift implementation on this step */
    const upliftActiveJSONResponse = await DataService.isUpliftActiveByBrand();
    const upliftActiveByBrand = {
      upliftActive: false,
      beachesActive: false
    };

    if(upliftActiveJSONResponse && Object.keys(upliftActiveJSONResponse).length > 0) {
      upliftActiveByBrand.upliftActive = upliftActiveJSONResponse.upliftActive;
      upliftActiveByBrand.beachesActive = upliftActiveJSONResponse.beachesActive;
    }

    const sessionStorageABTasty = sessionStorage.getItem('roomSuitesSortBy');
    const sortByABTasty = sessionStorageABTasty ? parseInt(sessionStorageABTasty, 10) : 0;
    const filters = {... this.state.filters };

    filters.sortBy = sortByABTasty;

    // Refactor after Willmer's change to dataservice method
    if (gatewaysResponse.code === 1) {
      const gateways = {};

      for (const item of gatewaysResponse.content) {
        gateways[item.gateway.toLowerCase()] = {
          city: item.city.toLowerCase(),
          airportName: item.airportName,
          gateway: item.gateway.toLowerCase(),
        };
      }

      this.setState({ gateways });
    }

    this.setState({filters: filters});

    const upliftAvailabilityJSON = upliftActiveByBrand.upliftActive;
    const upliftBeachesAvailabilityJSON = upliftActiveByBrand.beachesActive;
    const currentEnviroment = window._app.results.environment;
    const { isSandals = false } = window._app.brandOptions;
    let showUplift = true;

    if(!isSandals) {
      if(!upliftBeachesAvailabilityJSON) {
        showUplift = currentEnviroment !== 'PRD';
      }
    }

    if(upliftAvailabilityJSON && showUplift) {
      upliftJS(true, currentEnviroment);
      initUpliftNonPayment(this.checkUpliftStatus);
    }
  }

  checkUpliftStatus = (isAvailable) => {
    const upliftIsReady = this.state.upliftIsReady;

    if(!upliftIsReady) {
      this.setState({upliftIsReady: isAvailable});
    }
  }

  changeUpliftAlreadyExecuted = (value) => {
    this.setState({upliftAlreadyUp: value});
  }

  onBackButtonEvent = () => {
    const { ...fields } = this.state.fields;

    this.setState({ scrollableRoomId: fields.roomCode || '', previouslyViewedRoom: fields.roomCode });
  }

  fetchSummary = async() => {

    const { ...fields } = this.state.fields;

    this.showLoading();

    const summaryResponse = await DataService.readSummary();

    const {
      code,
      content: {
        ssgMember = {},
        vacationData = {},
        resortInfo = {},
        roomData = {},
        appliesFreeInsurance = false,
        selectedRoom,
        roomFilters = {}
      } = {}
    } = summaryResponse;

    if (code) {

      const {
        rstCode = '',
        checkInDate = '',
        checkOutDate = '',
        adults = 2,
        children = 0,
        infants = 0,
        airIncluded = false,
      } = vacationData;

      const { resortShortName = '' } = resortInfo;
      const summaryFilters = roomFilters;
      const filters = { ...this.state.filters, ...summaryFilters };
      const { ssgNumber = 0 } = ssgMember;

      const scrollableRoomId = roomData.roomCode || selectedRoom || '';
      const summaryRoomCode = roomData.roomCode || '';
      const previouslyViewedRoom = selectedRoom || '';

      fields.rstCode = rstCode;
      fields.roomCode = selectedRoom || roomData.roomCode || '';
      fields.checkInDate = checkInDate;
      fields.checkOutDate = checkOutDate;
      fields.adults = adults;
      fields.children = children;
      fields.infants = infants;
      fields.airIncluded = airIncluded;
      fields.ssgNumber = ssgNumber || 0;

      this.setState({
        fields,
        status: 'success',
        resortShortName,
        vacationData,
        appliesFreeInsurance,
        summaryResponse: summaryResponse.content,
        scrollableRoomId,
        summaryRoomCode,
        previouslyViewedRoom,
        filters,
      }, () => {
        this.fetchRooms();
      });
    }

  }

  checkAirAvailability({ startDate, endDate }) {
    const allPartiesAvailable = (startDate && endDate);
    const maxFlightDate = moment().add(329, 'days');
    const minFlightDate = moment().add(14, 'days');
    const showFlightWarning = allPartiesAvailable && ((endDate > maxFlightDate) || (startDate < minFlightDate));

    return showFlightWarning;

  }


  showRoomDetails = (roomCode = '') => {
    const fields = { ...this.state.fields, roomCode };

    this.setState({ fields, showRoomDetails: true, previouslyViewedRoom: roomCode, scrollableRoomId: roomCode, changeUpliftAlreadyExecuted: false });
  }

  updateFields = (newFields, cb = null) => {
    const { ...oldFields } = this.state.fields;

    const fields = { ...oldFields, ...newFields };

    this.setState({ fields }, cb);

  }

  updateScrollableRoomId = (scrollableRoomId) => {

    this.setState({ scrollableRoomId });
  }

  removeRoom = () => {

    this.setState(oldState => {
      const { ...fields } = oldState.fields;
      const scrollableRoomId = fields.roomCode;

      fields.roomCode = null;

      return {
        fields,
        scrollableRoomId,
        showRoomDetails: false,
      };
    });
  }

  resetScrollableRoomId = () => {
    this.setState({ scrollableRoomId: '' });
  }

  onDateChange = ({ startDate: rawStartDate, endDate: rawEndDate, scrollableRoomId }) => {


    const { ...fields } = this.state.fields;

    fields.checkInDate = rawStartDate && moment(rawStartDate._d).format(constants.DB_DATE_FORMAT);
    fields.checkOutDate = rawEndDate && moment(rawEndDate._d).format(constants.DB_DATE_FORMAT);

    this.setState({ fields, scrollableRoomId, hasNewDates: true, previouslyViewedRoom: scrollableRoomId }, () => {
      this.fetchRooms(this.applyFilters);
    });
  }

  onFilterChange = (value, name) => {

    const { ...filters } = this.state.filters;

    filters[name] = value.split(',', value.length).map(Number);

    this.setState({ filters }, this.applyFilters);
  }

  removeSelectedOption = (value, name) => {
    const { ...filters } = this.state.filters;

    const newFilter = filters[name].filter((filter) => filter !== value);

    filters[name] = newFilter;


    this.setState({ filters }, this.applyFilters);
  }

  sortByDropdown(sortBy = 0, roomsArray = []) {
    const rooms = [...roomsArray];
    const roomLevels = [0, 2, 1, 3];

    const sortEnum = {
      0: function priceLowest(roomA, roomB) {
        return roomA.afterPromoRate - roomB.afterPromoRate;
      },

      1: function priceHigest(roomA, roomB) {
        return roomB.afterPromoRate - roomA.afterPromoRate;
      },

      2: function availability(roomA, roomB) {
        const isCurrentAvailable = roomA.status === 'AVAILABLE';
        const isNextAvailable = roomB.status === 'AVAILABLE';

        if (!isCurrentAvailable && isNextAvailable) {
          return 1;
        }

        if (isCurrentAvailable && !isNextAvailable) {
          return -1;
        }

        return 0;
      },

      3: function categoryLowest(roomA, roomB) {
        if (!roomB.loveNest && !roomA.loveNest) {
          return roomLevels[roomB.roomTypeId] - roomLevels[roomA.roomTypeId];
        }

        if(!roomA.loveNest && roomB.loveNest) {
          return 1;
        }

        if (roomA.loveNest && !roomB.loveNest) {
          return -1;
        }

        return 0;
      },

      4: function categoryLowest(roomA, roomB) {
        if (!roomB.loveNest && !roomA.loveNest) {
          return roomLevels[roomA.roomTypeId] - roomLevels[roomB.roomTypeId];
        }

        if(roomA.loveNest && !roomB.loveNest) {
          return 1;
        }

        if (!roomA.loveNest && roomB.loveNest) {
          return -1;
        }

        return 0;
      }
    };

    const applySort = sortEnum[sortBy];

    const {status, upliftIsReady, vacationData, resortShortName} = this.state;

    if(status === 'success' && resortShortName) {
      const checkIn = vacationData.checkInDate.replace(/[-]/g, '');
      const checkOut = vacationData.checkOutDate.replace(/[-]/g, '');
      const resortName = resortShortName;
      const vacationContent = {
        checkInDate: checkIn,
        checkOutDate: checkOut,
        resortName: resortName
      };

      if(upliftIsReady) {
        setTimeout(() => {
          nonPaymentUplift(vacationContent);
        }, 1000);
      }
    }

    return rooms.sort(applySort).sort(sortEnum[2]);
  }

  handleSortBy = value => {

    const { ...filters } = this.state.filters;

    filters.sortBy = value;

    this.setState({ filters }, this.applyFilters);
  }

  applyFilters = () => {

    const { roomView, amenities, levels, sortBy, roomTypes } = this.state.filters;
    const { rooms } = this.state.roomData;
    const mismatchedRooms = [];
    const sortedRooms = this.sortByDropdown(sortBy, rooms);

    const roomsFiltered = sortedRooms
      .filter(room => {
        const allTrue = ([
          applyAmenitiesFilter({ room, values: amenities }),
          applyRoomViewFilter({ room, values: roomView }),
          applyLevelsFilter({ room, values: levels }),
          applyRoomTypeFilter({room, values: roomTypes})
        ].every(result => result));

        if (!allTrue) {
          mismatchedRooms.push({ ...room });
        }

        return allTrue;
      });

    this.setState({ roomsFiltered, mismatchedRooms }, this.disableRoomInputs);
  }

  resetFilters = () => {
    const filters = { ...this.state.filters };
    const roomData = { ...this.state.roomData };
    const roomsFiltered = [...roomData.rooms];

    filters.amenities = [];
    filters.roomView = [];
    filters.levels = [];
    filters.roomTypes = [];

    this.setState({ filters, roomsFiltered }, this.applyFilters);
  }

  disableRoomInputs = () => {

    const { roomCategoryViews: oldRoomCategoryViews, amenities: oldAmenities, roomTypes: oldRoomTypes, roomLevels: oldRoomLevels  } = this.state.roomInputs;
    const { roomCategoryViewsArray, amenitiesArray, roomLevelsArray, roomTypesArray } = this.getCurrentRoomInputs();
    const filters = {...this.state.filters};

    const _disableNotValidValues = (oldRoomInputArray, currentRoomInputArray) => {

      return oldRoomInputArray.map(obj => {

        const disabled = !currentRoomInputArray.includes(obj.value);

        const label = React.cloneElement(
          obj.label,
          {
            ...obj.label.props,
            className: disabled ? 'disabled' : ''
          },
        );

        const options = {
          value: obj.value,
          label: label,
          attrs: {
            disabled,
          }
        };

        return options;
      });
    };

    let roomLevelsFiltered = [];

    if(filters.levels.length > 0) {
      const filter = filters.levels.filter((level) => {
        return roomLevelsArray.includes(level);
      });

      roomLevelsFiltered = filter;
    }

    filters.levels = roomLevelsFiltered;

    this.setState({
      roomInputs: {
        roomCategoryViews: _disableNotValidValues(oldRoomCategoryViews, roomCategoryViewsArray),
        amenities: _disableNotValidValues(oldAmenities, amenitiesArray),
        roomLevels: _disableNotValidValues(oldRoomLevels, roomLevelsArray),
        roomTypes: _disableNotValidValues(oldRoomTypes, roomTypesArray),
      },
      filters
    });
  }

  getCurrentRoomInputs() {
    const { roomsFiltered } = this.state;

    const roomInputs = {
      roomCategoryViews: new Set(),
      amenities: new Set(),
      roomTypes: new Set(),
      roomLevels: new Set(),
    };

    roomsFiltered.reduce((acc, room) => {
      room.views.map(roomView => acc.roomCategoryViews.add(roomView.id));
      room.amenities.map(amenity => acc.amenities.add(amenity.id));
      room.roomTypes.map(roomType => acc.roomTypes.add(roomType.id));
      acc.roomLevels.add(room.roomTypeId);
      if (room.loveNest) {
        acc.roomLevels.add(0);
      }

      return acc;

    }, roomInputs);

    return {
      roomCategoryViewsArray: Array.from(roomInputs.roomCategoryViews),
      amenitiesArray: Array.from(roomInputs.amenities),
      roomLevelsArray: Array.from(roomInputs.roomLevels),
      roomTypesArray: Array.from(roomInputs.roomTypes),
    };
  }

  fetchRooms = async() => {
    const { ...summaryResponse = {} } = this.state.summaryResponse;
    const { ...vacationData = {} } = summaryResponse.vacationData || {};
    const { ...fields } = this.state.fields;
    const { ...roomInputs } = this.state.roomInputs;
    const params = _app.urlparams;
    let roomsFiltered = [];
    let roomData = {};
    let appliesFreeInsurance = false;

    const showFlightWarning = this.checkAirAvailability({
      startDate: moment(fields.checkInDate, constants.DB_DATE_FORMAT),
      endDate: moment(fields.checkOutDate, constants.DB_DATE_FORMAT)
    });

    if (showFlightWarning) {
      fields.airIncluded = false;
    }

    // else {
    //   fields.airIncluded = airIncluded;
    // }

    this.showLoading();

    const getRoomsPromise = DataService.getRooms(fields);

    const { status, data = {} } = await getRoomsPromise;

    if (this.state.hasNewDates && vacationData) {
      vacationData.checkInDate = fields.checkInDate;
      vacationData.checkOutDate = fields.checkOutDate;
      summaryResponse.vacationData = vacationData;
    }


    if (status === SUCCESS) {

      gatracking.createStep2DataLayer(summaryResponse, data);

      roomData = data;
      roomsFiltered = [...roomData.rooms];
      roomInputs.roomCategoryViews = this.createRoomInputsStructure(roomData.roomCategoryViews);
      roomInputs.amenities = this.createRoomInputsStructure(roomData.amenities);
      roomInputs.roomLevels = this.createRoomInputsStructure(roomData.levels);
      roomInputs.roomTypes = this.createRoomInputsStructure(roomData.roomTypes);

      appliesFreeInsurance = roomData.appliesFreeInsurance;

      // pushing the number of rooms for dataLayer 'dev/js/modules/gatracking.js'
      // if (typeof dataLayer !== 'undefined') {
      //   dataLayer[1].totalRooms = roomsFiltered.length;
      // }

      if (roomData.guestMessage) {
        this.setState({ isModalOpen: { infantToChildren: true } });
        this.setState({ guestMessage: roomData.guestMessage });
      }
    }

    this.setState(
      {
        roomData,
        appliesFreeInsurance,
        roomsFiltered,
        roomInputs,
        status: status,
        showFlightWarning,
        fields,
        summaryResponse,
        getRoomsPromise,
      },
      () => this.applyFilters()
    );


    if (params.bypassForm === '2') {
      const bypass = true;

      this.submitRoom(bypass);
    }
  }

  generateRoute(bypass, airIncluded) {
    if (bypass === true) {
      return 'flights';
    } else if (airIncluded) {
      return 'itineraries';
    }

    return 'guests';
  }

  submitRoom = async(bypass = false, room = {}) => {

    this._toggleLoader(true);
    const filters = { ...this.state.filters };

    const step2 = await DataService.processRoom({
      ...this.state.fields,
      ...filters,
    });

    const { airIncluded = false } = this.state.fields;

    const route = this.generateRoute(bypass, airIncluded);

    if(Object.keys(room).length > 0) {
      const vacationSummary = this.state.summaryResponse;
      const roomData = room;

      gatracking.trackRoom(roomData, vacationSummary);
    }


    this.setState({ step2 });

    if (step2.code) {
      const logInfo = this.state.fields;

      DataService.logFE({
        payload: `Select room button executed: Room Selected:${logInfo.roomCode} Resort:${logInfo.rstCode}`,
        uri: window.location.pathname,
        step: window._app.page.step,
      });
      // try {
      //   cm.cmCreateShopAction5Tag();
      // } catch (error) {
      //   console.warn(error);
      // }

      // This is to prevent a user to have 2 routes in window.history for the /room entry
      window.history.back();
      window.location.href = `/${route}/?subSessionId=${encodeURIComponent(window._app.page.subSessionId)}`;

      return;
    }

    this._toggleLoader(false);
  }

  _toggleLoader = (value) => {
    this.setState({ showLoader: value });
  }

  createRoomInputsStructure = (input) => {
    return input.map(obj => ({
      value: obj.id,
      label: <span>{obj.name.toLowerCase()}</span>,
      attrs: {
        disabled: false,
      }
    }));
  }

  toggleModal = (modalId) => {
    return () => {
      const { ...isModalOpen } = this.state.isModalOpen;

      if (typeof isModalOpen[modalId] === 'boolean') {

        isModalOpen[modalId] = !isModalOpen[modalId];

        this.setState({ isModalOpen });
      } else {
        throw new Error('Warning: Modal doesn\'t exists on the isModalOpen state!');
      }
    };
  }

  resetRoomInputs = () => {
    const { ...roomInputs } = this.state.roomInputs;

    const _setToDefault = (roomInput) => (
      roomInput.map(obj => ({
        value: obj.value,
        label: <span>{obj.label}</span>,
        attrs: {
          disabled: false,
        }
      }))
    );

    roomInputs.roomCategoryViews = _setToDefault(roomInputs.roomCategoryViews);

    roomInputs.amenities = _setToDefault(roomInputs.amenities);

    roomInputs.roomTypes = _setToDefault(roomInputs.roomTypes);

    this.setState({ roomInputs }, this.applyFilters);
  }

  showLoading = (status = 'loading') => {

    this.setState({ status });
  }

  toggleRoomInputs = () => {

    const showFilter = this.state.showInputs;

    if(showFilter) {
      document.querySelector('body').classList.remove('room-overflow-off');
    }else {
      document.querySelector('body').classList.add('room-overflow-off');
    }

    this.setState({ showInputs: !this.state.showInputs });
  }

  toggleRoomAvailability = (isSoldOut, roomCategory) => () => {

    let { currentRoomAvailability } = this.state;
    const isADifferentRoom = currentRoomAvailability !== roomCategory;

    if (isSoldOut) {
      currentRoomAvailability = isADifferentRoom ? roomCategory : '';
    }

    this.setState({ currentRoomAvailability });
  }

  closeRoomAvailability = () => {
    this.setState({ currentRoomAvailability: false });
  }

  closeModal = (name) => {

    return () => {
      const currentState = { ...this.state.isModalOpen };

      currentState[name] = false;

      if (name === 'forgotPassword') {
        this._resetSSGModal();
        this.toggleRequired(['username_recovery', 'email_recovery'])();
      }

      this.setState({ isModalOpen: currentState });
    };
  }

  displayLevelInformation = () => {
    const showLevelInfo = this.state.showLevelInfo;

    if(showLevelInfo) {
      document.querySelector('body').classList.remove('room-overflow-off');
    }else {
      document.querySelector('body').classList.add('room-overflow-off');
    }

    this.setState({ showLevelInfo: !showLevelInfo});
  }

  render() {
    const context = {
      state: this.state,
      onDateChange: this.onDateChange,
      onFilterChange: this.onFilterChange,
      removeSelectedOption: this.removeSelectedOption,
      resetFilters: this.resetFilters,
      resetRoomInputs: this.resetRoomInputs,
      fetchRooms: this.fetchRooms,
      updateFields: this.updateFields,
      submitRoom: this.submitRoom,
      toggleModal: this.toggleModal,
      toggleRoomInputs: this.toggleRoomInputs,
      removeRoom: this.removeRoom,
      resetScrollableRoomId: this.resetScrollableRoomId,
      updateScrollableRoomId: this.updateScrollableRoomId,
      showRoomDetails: this.showRoomDetails,
      toggleRoomAvailability: this.toggleRoomAvailability,
      closeRoomAvailability: this.closeRoomAvailability,
      closeModal: this.closeModal,
      handleSortBy: this.handleSortBy,
      changeUpliftAlreadyExecuted: this.changeUpliftAlreadyExecuted,
      displayLevelInformation: this.displayLevelInformation
    };

    return (
      <RoomContext.Provider value={context}>
        {this.props.children}
      </RoomContext.Provider>
    );
  }
}

export { RoomContext, RoomProvider };



// WEBPACK FOOTER //
// ./js/components/step2/RoomContext.js